import { useCallback, useMemo } from 'react';
import { useLocalDateKey, usePersistentState } from '../../lib/storage';
import {
  createId,
  isUnknownArray,
  migrateMedia,
  sortMediaItems,
  type MediaItem,
  type MediaType,
} from '../../lib/dailies';

const NO_ITEMS: readonly unknown[] = [];
const MAX_TITLE = 60;

export interface MediaState {
  /** Local calendar date key. Changes at midnight WITHOUT a reload. */
  readonly todayKey: string;
  /** Sorted for display. Never filtered — nothing here is ever auto-removed. */
  readonly items: readonly MediaItem[];
  readonly add: (title: string, releaseDate: string, type: MediaType) => void;
  readonly remove: (id: string) => void;
}

/**
 * `my-hub:media:items` on `usePersistentState` — the tracker does NOT reset
 * daily. Putting it on `useDailyState` would erase the user's whole watchlist
 * every midnight; that split is the single most destructive mistake available
 * in this panel, so it is called out here rather than left to the key name.
 *
 * `useLocalDateKey()` supplies `todayKey`, so the release status of every row
 * is re-derived when the shared midnight ticker fires: an item flips from bold
 * "TODAY" to struck-through "yesterday" in a pinned tab with no refresh.
 */
export function useMedia(): MediaState {
  const todayKey = useLocalDateKey();
  const [raw, setRaw] = usePersistentState<readonly unknown[]>(
    'media',
    'items',
    NO_ITEMS,
    isUnknownArray,
  );

  const items = useMemo(() => migrateMedia(raw), [raw]);
  const sorted = useMemo(() => sortMediaItems(items, todayKey), [items, todayKey]);

  const add = useCallback(
    (title: string, releaseDate: string, type: MediaType) => {
      const clean = title.trim().slice(0, MAX_TITLE);
      if (clean === '') return;
      // releaseDate is stored EXACTLY as <input type="date"> produced it:
      // a bare 'YYYY-MM-DD', or '' if the user left it blank. No Date round
      // trip — that is what shifts every item a day in a negative UTC offset.
      setRaw([...items, { id: createId('media'), title: clean, releaseDate, type }]);
    },
    [items, setRaw],
  );

  const remove = useCallback(
    (id: string) => setRaw(items.filter((item) => item.id !== id)),
    [items, setRaw],
  );

  return { todayKey, items: sorted, add, remove };
}

export { MAX_TITLE };
